/**
 * A RAÇÃO SE ESCOLHE, NÃO SE DIGITA.
 *
 * Duas fileiras de botões: o tamanho do pélete e a proteína. Qualquer
 * combinação das duas vale — a flexibilidade está em combinar, não em digitar.
 *
 * POR QUE LISTA FECHADA. Campo livre aceita «6a8», «6 a 8», «6-8» e «6.5», e
 * em seis meses o estoque tem quatro rações que são a mesma coisa escrita de
 * quatro jeitos, mais uma que não existe. Nenhum relatório se recupera disso,
 * porque o erro não parece erro: cada linha, sozinha, está plausível.
 *
 * O QUE ESTÁ NA LISTA VEM DO BANCO, e não daqui: `racao.granulometrias` e
 * `racao.proteinas`, em `parametros`. Outra piscicultura compra outros
 * tamanhos, e trocar a lista não pode exigir publicar o app.
 *
 * A TABELA DE ARRAÇOAMENTO SUGERE; QUEM DECIDE É QUEM ESTÁ LÁ. A sugestão
 * vem marcada, e trocar é um toque. Foi o A-1 que ensinou: a tabela dizia
 * 32% e o galpão tinha 36%, e a tabela é média enquanto o galpão é específico.
 */

import type { ParametroDoCalculo } from '../acesso/api'
import { numero } from '../dominio/unidades'

export interface Especificacao {
  min: number
  max: number
  proteina: number
}

/** «1,4» vira 1,4 a 1,4; «6-8» vira 6 a 8. */
function lerFaixa(texto: string): { min: number; max: number } | null {
  const partes = texto.split('-').map((x) => Number(x.trim().replace(',', '.')))
  const min = partes[0]
  const max = partes.length > 1 ? partes[1] : partes[0]
  if (min === undefined || max === undefined) return null
  if (!Number.isFinite(min) || !Number.isFinite(max) || min <= 0 || max < min) return null
  return { min, max }
}

function listaDoParametro(
  parametros: ParametroDoCalculo[],
  chave: string,
  padrao: string,
): string[] {
  const achado = parametros.find((p) => p.chave === chave)
  return (achado?.valor ?? padrao)
    .split(';')
    .map((x) => x.trim())
    .filter((x) => x !== '')
}

export function granulometrias(parametros: ParametroDoCalculo[]): { rotulo: string; min: number; max: number }[] {
  return listaDoParametro(parametros, 'racao.granulometrias', '1,4;1,8;2,4;3-4;4-6;6-8;8-10')
    .map((rotulo) => {
      const faixa = lerFaixa(rotulo)
      return faixa ? { rotulo, ...faixa } : null
    })
    .filter((x): x is { rotulo: string; min: number; max: number } => x !== null)
}

export function proteinas(parametros: ParametroDoCalculo[]): number[] {
  return listaDoParametro(parametros, 'racao.proteinas', '45;40;36;32;28;24')
    .map((x) => Number(x.replace(',', '.')))
    .filter((x) => Number.isFinite(x) && x > 0 && x <= 100)
}

/** «6 a 8 mm · 28%» — como a ração se chama quando não tem nome comercial. */
export function nomeDaRacao(e: Especificacao | null): string {
  if (!e) return '—'
  const faixa = e.min === e.max ? `${numero(e.min, 1)} mm` : `${numero(e.min, 1)} a ${numero(e.max, 1)} mm`
  return `${faixa} · ${numero(e.proteina, 0)}%`
}

export function EscolherRacao({
  parametros, escolhida, sugerida, aoEscolher, compacto = false,
}: {
  parametros: ParametroDoCalculo[]
  escolhida: Especificacao | null
  /** O que a tabela de arraçoamento indicou, para marcar como «sugerida». */
  sugerida?: Especificacao | null
  aoEscolher: (e: Especificacao) => void
  compacto?: boolean
}) {
  const gs = granulometrias(parametros)
  const ps = proteinas(parametros)
  const atual = escolhida ?? sugerida ?? null

  const mesmaFaixa = (g: { min: number; max: number }) =>
    atual !== null && Math.abs(atual.min - g.min) < 0.01 && Math.abs(atual.max - g.max) < 0.01

  return (
    <div className={compacto ? 'racao-escolha compacta' : 'racao-escolha'}>
      <span className="campo-rotulo">Tamanho do pélete</span>
      <div className="racao-linha">
        {gs.map((g) => (
          <button
            key={g.rotulo}
            type="button"
            className={mesmaFaixa(g) ? 'racao-botao ativo' : 'racao-botao'}
            aria-pressed={mesmaFaixa(g)}
            onClick={() =>
              aoEscolher({ min: g.min, max: g.max, proteina: atual?.proteina ?? ps[0] ?? 28 })
            }
          >
            {g.rotulo}
          </button>
        ))}
      </div>

      <span className="campo-rotulo">Proteína</span>
      <div className="racao-linha">
        {ps.map((p) => (
          <button
            key={p}
            type="button"
            className={atual?.proteina === p ? 'racao-botao ativo' : 'racao-botao'}
            aria-pressed={atual?.proteina === p}
            onClick={() =>
              aoEscolher({
                min: atual?.min ?? gs[0]?.min ?? 6,
                max: atual?.max ?? gs[0]?.max ?? 8,
                proteina: p,
              })
            }
          >
            {numero(p, 0)}%
          </button>
        ))}
      </div>
    </div>
  )
}
